import { useState, type MouseEvent } from 'react'
import { formatMoney, withCountRu } from '@dorixona/utils'
import { formatShortSum, niceTop, type Bar } from './model'

const W = 680
const H = 250
const L = 52
const R = 10
const T = 16
const B = 30
const PW = W - L - R
const PH = H - T - B

interface Tip {
  x: number
  y: number
  bar: Bar
}

/**
 * Выручка по дням. Рисунок для чтения с экрана недоступен, поэтому те же
 * цифры идут рядом таблицей без визуального представления: график остаётся
 * картинкой, а данные — данными (HIG accessibility.md › Charts).
 *
 * Цвета — в screens/dashboard.css ролями кита, а не атрибутами: так столбцы,
 * сетка и подписи меняются вместе с темой.
 */
export function RevenueChart({ bars }: { bars: Bar[] }) {
  const [tip, setTip] = useState<Tip | null>(null)
  const max = Math.max(...bars.map((b) => b.sum), 1)
  const top = niceTop(max)
  const bw = PW / Math.max(bars.length, 1)
  const pad = Math.min(3, bw * 0.12)
  const every = Math.ceil(Math.max(bars.length, 1) / 14)

  return (
    <div className="dsh-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Выручка по дням">
        {[0, 1, 2, 3].map((g) => {
          const y = T + PH - (PH * g) / 3
          return (
            <g key={g}>
              <line className="grid" x1={L} y1={y} x2={W - R} y2={y} />
              <text x={L - 8} y={y + 4} textAnchor="end" fontSize="11">
                {formatShortSum((top * g) / 3)}
              </text>
            </g>
          )
        })}
        {bars.map((bar, i) => {
          const h = Math.max((PH * bar.sum) / top, bar.sum ? 3 : 0)
          const x = L + i * bw + pad
          const w = bw - 2 * pad
          const y = T + PH - h
          const r = Math.min(4, h)
          const show = (event: MouseEvent) => {
            setTip({ x: event.clientX, y: event.clientY, bar })
          }
          return (
            <g key={bar.label}>
              <g
                className="bar"
                data-day={bar.label}
                onMouseMove={show}
                onMouseLeave={() => {
                  setTip(null)
                }}
              >
                <rect x={L + i * bw} y={T} width={bw} height={PH} fill="transparent" />
                <path
                  d={`M${x} ${T + PH} V${y + r} Q${x} ${y} ${x + r} ${y} H${x + w - r} Q${x + w} ${y} ${x + w} ${y + r} V${T + PH} Z`}
                />
                {bar.sum === max && bar.sum ? (
                  <text x={x + w / 2} y={y - 6} className="peak" textAnchor="middle" fontSize="11">
                    {formatShortSum(bar.sum)}
                  </text>
                ) : null}
              </g>
              {i % every === 0 ? (
                <text x={x + w / 2} y={H - 9} textAnchor="middle" fontSize="11">
                  {bar.label}
                </text>
              ) : null}
            </g>
          )
        })}
      </svg>
      <table className="visually-hidden">
        <caption>Выручка по дням</caption>
        <tbody>
          {bars.map((bar) => (
            <tr key={bar.label}>
              <th scope="row">{bar.label}</th>
              <td>{formatMoney(bar.sum)}</td>
              <td>{withCountRu(bar.n, ['заказ', 'заказа', 'заказов'])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {tip ? (
        <div
          className="dsh-tip"
          style={{ left: Math.min(tip.x + 14, window.innerWidth - 180), top: tip.y - 14 }}
        >
          <b>{tip.bar.label}</b>
          {formatMoney(tip.bar.sum)} · {withCountRu(tip.bar.n, ['заказ', 'заказа', 'заказов'])}
        </div>
      ) : null}
    </div>
  )
}
